import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import Checkbox from '../../shared/Checkbox';
import Image from "next/legacy/image";
import difference from 'lodash/difference';
import filter from 'lodash/filter';

type StepOneDisabledState = {
  realistic: boolean;
  investigative: boolean;
  artistic: boolean;
  social: boolean;
  enterprising: boolean;
  conventional: boolean;
};
const allKeys = [
  'realistic',
  'investigative',
  'artistic',
  'social',
  'enterprising',
  'conventional',
];
interface StepOneProps {
  error?: string;
}

const StepOne: React.FC<StepOneProps> = ({ error }) => {
  const context = useFormContext();

  // const watch = watchForm();
  const watch = context.watch('style');

  const calculateDisabled = (watch: any) => {
    const trueKeys = filter(
      allKeys,
      (key: string) =>
        watch &&
        watch.length &&
        Array.isArray(watch) &&
        watch?.map((quality: string) => quality.toLowerCase()).includes(key),
    );
    const falseKeys = difference(allKeys, trueKeys);
    const disabled = {};
    if (trueKeys.length == 3) {
      falseKeys.forEach((value: any) => {
        (disabled as any)[value] = true;
      });
      trueKeys.forEach((value: any) => {
        (disabled as any)[value] = false;
      });
    } else {
      allKeys.forEach((value: any) => {
        (disabled as any)[value] = false;
      });
    }
    return disabled;
  };
  const isDisabled = useMemo(
    () => calculateDisabled(watch) as StepOneDisabledState,
    [watch],
  );

  return (
    <>
      <Image
        src="/images/career-quiz-step-1.svg"
        blurDataURL="/images/career-quiz-step-1.svg"
        layout="responsive"
        loading="eager"
        placeholder="blur"
        width={2500}
        height={2000}
        alt="Icon of a person sitting at a computer"
        className="self-center block w-32 mx-auto my-2 md:my-0 md:w-44"
      />
      <div className="p-2 md:p-8">
        <h1 className="text-2xl font-semibold text-center text-careerPageHeading md:text-left md:text-3xl">
          Which <span className="text-careerPageHighlight">work style</span> do
          you identify as?
        </h1>
        <p className="my-4 text-center text-careerPageHeading md:text-left md:my-1">
          Choose up to 3.
        </p>
        {error && <p className="mt-4 italic font-semibold text-red">{error}</p>}
        <form className="grid grid-cols-1 my-4 -mx-2 md:grid-cols-2">
          <div className="px-2 my-2">
            <Checkbox
              methods={context}
              name="style"
              value="Realistic"
              id="realistic"
              disabled={isDisabled.realistic}
              className="peer-checked:bg-careerPageHighlight peer-checked:border-careerPageHighlight border-careerPageHeading"
            >
              <p className="text-lg font-bold text-careerPageHeading">
                Realistic
              </p>
              <p className="text-sm text-careerPageHeading">
                You like working with objects, machines, tools, plants or
                animals, and like to be outdoors or perform athletic activities.{' '}
              </p>
            </Checkbox>
          </div>
          <div className="px-2 my-2">
            <Checkbox
              methods={context}
              name="style"
              value="Investigative"
              id="investigative"
              disabled={isDisabled.investigative}
              className="peer-checked:bg-careerPageHighlight peer-checked:border-careerPageHighlight border-careerPageHeading"
            >
              <p className="text-lg font-bold text-careerPageHeading">
                Investigative
              </p>
              <p className="text-sm text-careerPageHeading">
                You like to observe, learn, investigate, analyze, and solve
                problems.{' '}
              </p>
            </Checkbox>
          </div>
          <div className="px-2 my-2">
            <Checkbox
              methods={context}
              name="style"
              value="Social"
              id="social"
              disabled={isDisabled.social}
              className="peer-checked:bg-careerPageHighlight peer-checked:border-careerPageHighlight border-careerPageHeading"
            >
              <p className="text-lg font-bold text-careerPageHeading">Social</p>
              <p className="text-sm text-careerPageHeading">
                You like to work with people to inspire, inform, help, train or
                cure them.{' '}
              </p>
            </Checkbox>
          </div>
          <div className="px-2 my-2">
            <Checkbox
              methods={context}
              name="style"
              value="Artistic"
              id="artistic"
              disabled={isDisabled.artistic}
              className="peer-checked:bg-careerPageHighlight peer-checked:border-careerPageHighlight border-careerPageHeading"
            >
              <p className="text-lg font-bold text-careerPageHeading">
                Artistic
              </p>
              <p className="text-sm text-careerPageHeading">
                You like to work in unstructured situations using your
                imagination and creativity.{' '}
              </p>
            </Checkbox>
          </div>
          <div className="px-2 my-2">
            <Checkbox
              methods={context}
              name="style"
              value="Enterprising"
              id="enterprising"
              disabled={isDisabled.enterprising}
              className="peer-checked:bg-careerPageHighlight peer-checked:border-careerPageHighlight border-careerPageHeading"
            >
              <p className="text-lg font-bold text-careerPageHeading">
                Enterprising
              </p>
              <p className="text-sm text-careerPageHeading">
                You like to work with people to influence, persuade and lead
                them, and to achieve organizational or financial goals.{' '}
              </p>
            </Checkbox>
          </div>
          <div className="px-2 my-2">
            <Checkbox
              methods={context}
              name="style"
              value="Conventional"
              id="conventional"
              disabled={isDisabled.conventional}
              className="peer-checked:bg-careerPageHighlight peer-checked:border-careerPageHighlight border-careerPageHeading"
            >
              <p className="text-lg font-bold text-careerPageHeading">
                Conventional
              </p>
              <p className="text-sm text-careerPageHeading">
                You like to work with information, carry out detailed tasks, and
                have clerical or numerical interests.
              </p>
            </Checkbox>
          </div>
        </form>
      </div>
    </>
  );
};

export default StepOne;
